import type { DisputeRecommendation, StaffChargeback } from '@/lib/api/fraud-types'

const NEUTRAL = 'bg-neutral-100 text-neutral-700 dark:bg-neutral-800 dark:text-neutral-300'
const BLUE = 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300'
const AMBER = 'bg-amber-100 text-amber-800 dark:bg-amber-900/20 dark:text-amber-400'
const GREEN = 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
const RED = 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'

/** Which response filter a row falls under. */
export type ResponseGroup = 'open' | 'sent' | 'missed' | 'none'

export interface ResponseStatus {
  label: string
  chip: string
  group: ResponseGroup
  /** Hover text: what the status means and what to do. */
  title: string
}

export const RESPONSE_FILTERS: { value: '' | ResponseGroup; label: string }[] = [
  { value: '', label: 'All responses' },
  { value: 'open', label: 'Awaiting our response' },
  { value: 'sent', label: 'Response sent' },
  { value: 'missed', label: 'Closed with no response' },
]

export const RECOMMENDATION_META: Record<DisputeRecommendation, { label: string; chip: string }> = {
  challenge: { label: 'Challenge', chip: BLUE },
  accept: { label: 'Accept', chip: NEUTRAL },
  needs_human: { label: 'Needs review', chip: AMBER },
}

/** Finix's states once a dispute is decided or no longer open to an answer. */
const CLOSED_STATES = new Set(['won', 'lost', 'accepted'])

/**
 * How a dispute is being answered, from the fields the chargebacks list
 * carries for staff. Null for a row without them (an organizer's view).
 */
export function responseStatus(c: StaffChargeback): ResponseStatus | null {
  if (c.response_action === undefined) return null
  if (c.response_action === 'submitted') {
    return { label: 'Evidence submitted', chip: GREEN, group: 'sent', title: 'Our evidence and note went to the issuer through Finix.' }
  }
  if (c.response_action === 'accepted') {
    return { label: 'Accepted', chip: NEUTRAL, group: 'sent', title: 'We accepted liability at Finix.' }
  }
  if (c.response_action === 'submitting' || c.response_action === 'accepting') {
    return {
      label: 'Sending…', chip: AMBER, group: 'open',
      title: 'The call to Finix is in flight, or died mid-call. The sync job settles it from Finix; nothing to do yet.',
    }
  }
  if (!c.provider_dispute_id) {
    return { label: 'Not filed yet', chip: NEUTRAL, group: 'none', title: 'Recorded by hand; Finix has no dispute for it yet.' }
  }
  const finix = c.provider_response_state ?? ''
  if (finix === 'RESPONDED' || finix === 'ACCEPTED') {
    return { label: 'Answered in Finix', chip: GREEN, group: 'sent', title: 'Answered in the Finix dashboard, not from here.' }
  }
  if (finix === 'NEEDS_RESPONSE') {
    if (!c.has_evidence) {
      return { label: 'Not drafted', chip: NEUTRAL, group: 'open', title: 'The daily job has not drafted evidence yet. Open it to draft now.' }
    }
    const meta = c.recommendation ? RECOMMENDATION_META[c.recommendation] : null
    if (!meta) return { label: 'Drafted', chip: BLUE, group: 'open', title: 'Evidence drafted; review and answer.' }
    return {
      label: c.recommendation === 'needs_human' ? meta.label : `Draft: ${meta.label.toLowerCase()}`,
      chip: meta.chip,
      group: 'open',
      title: c.recommendation === 'needs_human'
        ? 'The rules could not decide: review the evidence before answering.'
        : `Evidence drafted, recommended to ${meta.label.toLowerCase()}. Review and send.`,
    }
  }
  if (finix === 'NO_RESPONSE_ALLOWED') {
    return { label: 'No response allowed', chip: NEUTRAL, group: 'none', title: 'Finix does not take a response for this dispute.' }
  }
  if (CLOSED_STATES.has(c.state) && c.state !== 'won') {
    return { label: 'No response sent', chip: RED, group: 'missed', title: 'Closed without an answer from us.' }
  }
  return null
}

/** Days until a deadline, rounded up; negative once it passed. */
export function daysUntil(iso: string | null | undefined, now = Date.now()): number | null {
  if (!iso) return null
  const ms = new Date(iso).getTime() - now
  return Number.isNaN(ms) ? null : Math.ceil(ms / 86_400_000)
}
